import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { MultiImageUploadField } from "@/components/admin/MultiImageUploadField";
import { YoutubeVideosField } from "@/components/admin/YoutubeVideosField";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { CTAManager } from "@/components/admin/CTAManager";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "", excerpt: "", content: "", featured_image: "",
  gallery_images: [], youtube_videos: [], event_date: "", event_location: "",
  ctas: [], author: "PhysioAbhi Team", published: true,
};

export function BlogManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/blog").then((res) => setItems(res.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title, excerpt: item.excerpt || "", content: item.content,
      featured_image: item.featured_image || "", gallery_images: item.gallery_images || [],
      youtube_videos: item.youtube_videos || [], event_date: item.event_date || "",
      event_location: item.event_location || "", ctas: item.ctas || [],
      author: item.author || "", published: item.published,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/admin/blog/${editing.id}`, form);
      else await api.post("/admin/blog", form);
      toast.success("Saved successfully.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="blog-add-button" className="rounded-none bg-accent text-accent-foreground">
          <Plus className="h-4 w-4 mr-2" /> Add Post
        </Button>
      </div>
      <div className="bg-background border border-border divide-y divide-border">
        {items.map((item) => (
          <div key={item.id} data-testid={`blog-manager-row-${item.id}`} className="flex items-center gap-4 p-4">
            {item.featured_image && <img src={resolveImageUrl(item.featured_image)} alt="" className="w-12 h-12 object-cover shrink-0" />}
            <div className="flex-1">
              <p className="font-medium">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.published ? "Published" : "Draft"} · {item.author} · {item.ctas?.length || 0} CTA(s)</p>
            </div>
            <Button size="icon" variant="outline" className="rounded-none" data-testid={`blog-edit-${item.id}`} onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="rounded-none" data-testid={`blog-delete-${item.id}`} onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-muted-foreground text-sm">No blog posts yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Blog Post</DialogTitle></DialogHeader>
          <Tabs defaultValue="content">
            <TabsList className="rounded-none bg-secondary border border-border flex-wrap h-auto">
              <TabsTrigger value="content" data-testid="blog-form-tab-content" className="rounded-none">Content</TabsTrigger>
              <TabsTrigger value="media" data-testid="blog-form-tab-media" className="rounded-none">Media</TabsTrigger>
              <TabsTrigger value="event" data-testid="blog-form-tab-event" className="rounded-none">Event Info</TabsTrigger>
              <TabsTrigger value="ctas" data-testid="blog-form-tab-ctas" className="rounded-none">CTAs</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-4 pt-4">
              <div><Label>Title</Label><Input data-testid="blog-form-title-input" className="rounded-none mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Excerpt</Label><Input data-testid="blog-form-excerpt-input" className="rounded-none mt-1.5" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
              <div>
                <Label>Content</Label>
                <div className="mt-1.5">
                  <RichTextEditor testId="blog-form-content-input" value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
                </div>
              </div>
              <div><Label>Author</Label><Input data-testid="blog-form-author-input" className="rounded-none mt-1.5" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
              <div className="flex items-center gap-2">
                <input id="blog-published" type="checkbox" data-testid="blog-form-published-checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                <Label htmlFor="blog-published" className="text-sm">Published (visible on public blog)</Label>
              </div>
            </TabsContent>

            <TabsContent value="media" className="space-y-6 pt-4">
              <div>
                <Label>Featured Image</Label>
                <div className="mt-1.5"><ImageUploadField testId="blog-form-featured-image-input" value={form.featured_image} onChange={(v) => setForm({ ...form, featured_image: v })} /></div>
              </div>
              <div>
                <Label>Gallery Images</Label>
                <div className="mt-1.5"><MultiImageUploadField testId="blog-form-gallery-images" images={form.gallery_images} onChange={(v) => setForm({ ...form, gallery_images: v })} /></div>
              </div>
              <div>
                <Label>YouTube Videos</Label>
                <div className="mt-1.5"><YoutubeVideosField testId="blog-form-videos" videos={form.youtube_videos} onChange={(v) => setForm({ ...form, youtube_videos: v })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="event" className="space-y-4 pt-4">
              <p className="text-xs text-muted-foreground">Optional — fill these in if this post covers a specific activity or event.</p>
              <div><Label>Event Date</Label><Input placeholder="e.g. 2026-08-15" data-testid="blog-form-event-date-input" className="rounded-none mt-1.5" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
              <div><Label>Event Location</Label><Input data-testid="blog-form-event-location-input" className="rounded-none mt-1.5" value={form.event_location} onChange={(e) => setForm({ ...form, event_location: e.target.value })} /></div>
            </TabsContent>

            <TabsContent value="ctas" className="pt-4">
              <CTAManager ctas={form.ctas} onChange={(v) => setForm({ ...form, ctas: v })} />
            </TabsContent>
          </Tabs>
          <Button onClick={handleSave} data-testid="blog-form-save-button" className="w-full rounded-none bg-accent text-accent-foreground mt-4">Save Post</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
